import {
  CHAT_REPORT_REASON_LABELS,
  ChatReportReasonEnum,
} from '@src/features/shared/community/enums/chatReportReason';
import Modal from '@src/layouts/Modal';
import Button from '@src/ui/elements/button/Button';
import React, { useState } from 'react';

export interface ReportModalProps {
  isOpen?: boolean;
  onClose: () => void;
  onSubmit: (reason: ChatReportReasonEnum) => void;
}

const ReportModal: React.FC<ReportModalProps> = ({ isOpen = true, onClose, onSubmit }) => {
  const [selectedReason, setSelectedReason] = useState<ChatReportReasonEnum | null>(null);
  const [isReported, setIsReported] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const reportOptions = Object.values(ChatReportReasonEnum)
    .filter((value): value is ChatReportReasonEnum => typeof value === 'number')
    .map((value) => ({
      value,
      label: CHAT_REPORT_REASON_LABELS[value],
    }));

  const onClickReport = () => {
    if (!selectedReason) {
      return;
    }
    setIsReported(true);
    onSubmit(selectedReason);
  };

  return (
    <React.Fragment>
      {isOpen && (
        <Modal width='clamp(400px, 90vw, 420px)'>
          <div className='relative flex grow flex-col gap-8 px-8 py-10'>
            <button
              onClick={onClose}
              className='absolute right-4 top-4 flex size-8 items-center justify-center rounded-full text-black transition-colors hover:bg-gray-100 hover:text-gray-600'
              aria-label='Close modal'
            >
              <svg
                className='size-5'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>

            <div className='border-b border-b-gray-350 pb-4 pr-8'>
              <h2 className='text-lg font-semibold'>報告内容</h2>
            </div>

            <div className='whitespace-pre-wrap text-left'>
              <div className='space-y-3'>
                {reportOptions.map((option) => (
                  <label
                    key={option.value}
                    className='flex cursor-pointer items-center'
                  >
                    <input
                      type='radio'
                      name='reportReason'
                      value={option.value}
                      checked={selectedReason === option.value}
                      onChange={() => setSelectedReason(option.value)}
                      className='mr-3 size-3'
                    />
                    <span className='text-sm'>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className='flex justify-center'>
              <label
                className={`flex cursor-pointer items-center ${!selectedReason ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <input
                  type='checkbox'
                  checked={isConfirmed}
                  disabled={!selectedReason}
                  onChange={(e) => setIsConfirmed(e.target.checked)}
                  className='mr-3 size-3 disabled:cursor-not-allowed disabled:opacity-50'
                />
                <span className='text-xs'>報告内容を確認しました</span>
              </label>
            </div>

            <div className='mt-auto flex w-full flex-row justify-center gap-2 sm:flex-col sm:justify-start sm:gap-5'>
              <div className='w-[180px] sm:w-auto'>
                <Button
                  outline
                  fullRounded
                  fullWidth
                  variant='secondary'
                  onClick={onClose}
                  className='whitespace-nowrap'
                >
                  キャンセル
                </Button>
              </div>
              <div className='w-[180px] sm:w-auto'>
                <Button
                  outline
                  fullRounded
                  fullWidth
                  disabled={!selectedReason || !isConfirmed || isReported}
                  onClick={onClickReport}
                  className={`whitespace-nowrap ${!selectedReason || !isConfirmed || isReported ? 'disabled:!text-white' : ''}`}
                >
                  報告する
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </React.Fragment>
  );
};

export default ReportModal;
